import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  TrendingUp, Clock, DollarSign, Ship, Loader2,
  RefreshCw, AlertTriangle, Anchor
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, Cell, LineChart, Line
} from 'recharts';

const HORIZON_LABELS = { '7d': '7 Day', '30d': '30 Day', '90d': '90 Day', 'annual': 'Annual' };

export default function ForecastingPage() {
  const [demurrageForecasts, setDemurrageForecasts] = useState({});
  const [secondaryCosts, setSecondaryCosts] = useState(null);
  const [freightRates, setFreightRates] = useState([]);
  const [freightHistory, setFreightHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [sc, fh] = await Promise.all([
        api.get('/forecasting/secondary-costs'),
        api.get('/forecasting/freight-rates'),
      ]);
      setSecondaryCosts(sc.data);
      setFreightHistory(fh.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleDemurrageForecast = async (horizon) => {
    setGenerating(horizon);
    try {
      const { data } = await api.post(`/forecasting/demurrage/${horizon}`, {});
      setDemurrageForecasts(prev => ({ ...prev, [horizon]: data }));
    } catch (e) { console.error(e); }
    finally { setGenerating(null); }
  };

  const handleGenerateAllForecasts = async () => {
    setGenerating('all');
    try {
      const horizons = ['7d', '30d', '90d', 'annual'];
      const results = {};
      for (const h of horizons) {
        const { data } = await api.post(`/forecasting/demurrage/${h}`, {});
        results[h] = data;
      }
      setDemurrageForecasts(results);
    } catch (e) { console.error(e); }
    finally { setGenerating(null); }
  };

  const handleGenerateFreight = async () => {
    setGenerating('freight');
    try {
      const { data } = await api.post('/forecasting/freight-rates');
      setFreightRates(data);
      const { data: fh } = await api.get('/forecasting/freight-rates');
      setFreightHistory(fh);
    } catch (e) { console.error(e); }
    finally { setGenerating(null); }
  };

  const categories = secondaryCosts?.categories || {};
  const catChartData = Object.entries(categories).map(([k, v]) => ({
    name: k.replace(/_/g, ' '),
    predicted: v.predicted_30d,
    anomaly: v.anomaly_flag,
  }));

  // Group freight by route for chart
  const routeGroups = {};
  (freightHistory.length > 0 ? freightHistory : freightRates).forEach(p => {
    if (!routeGroups[p.route]) routeGroups[p.route] = [];
    routeGroups[p.route].push(p);
  });
  const freightChartData = Object.entries(routeGroups).slice(0, 8).map(([route, preds]) => {
    const spot7 = preds.find(p => p.horizon_days === 7);
    return {
      route: route.length > 20 ? route.substring(0, 18) + '...' : route,
      spot_7d: spot7 ? parseFloat(spot7.predicted_spot_rate) : 0,
    };
  });

  if (loading) {
    return (
      <div className="p-6 md:p-8 space-y-6" data-testid="forecasting-loading">
        <Skeleton className="h-8 w-56" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">{[1,2,3,4].map(i => <Skeleton key={i} className="h-28" />)}</div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="forecasting-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tighter text-slate-950">Cost Forecasting</h1>
          <p className="text-sm text-slate-500 mt-1">Demurrage, secondary cost & freight rate predictions</p>
        </div>
      </div>

      <Tabs defaultValue="demurrage">
        <TabsList className="rounded-sm h-9 bg-slate-100 p-0.5">
          <TabsTrigger value="demurrage" className="rounded-sm text-xs flex-1" data-testid="tab-demurrage-forecast">
            <Clock className="h-3 w-3 mr-1" />Demurrage
          </TabsTrigger>
          <TabsTrigger value="secondary" className="rounded-sm text-xs flex-1" data-testid="tab-secondary-costs">
            <DollarSign className="h-3 w-3 mr-1" />Secondary Costs
          </TabsTrigger>
          <TabsTrigger value="freight" className="rounded-sm text-xs flex-1" data-testid="tab-freight-rates">
            <Ship className="h-3 w-3 mr-1" />Freight Rates
          </TabsTrigger>
        </TabsList>

        {/* Demurrage Tab */}
        <TabsContent value="demurrage">
          <div className="space-y-4">
            <div className="flex justify-end">
              <Button
                onClick={handleGenerateAllForecasts}
                disabled={generating === 'all'}
                data-testid="generate-all-forecasts-button"
                className="rounded-sm bg-[#0A2463] hover:bg-[#0A2463]/90 text-white text-xs"
              >
                {generating === 'all' ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
                Generate All Horizons
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {['7d', '30d', '90d', 'annual'].map(h => {
                const fc = demurrageForecasts[h];
                return (
                  <Card key={h} className="bg-white border border-slate-200 rounded-sm shadow-none" data-testid={`demurrage-card-${h}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">{HORIZON_LABELS[h]}</p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDemurrageForecast(h)}
                          disabled={generating === h}
                          data-testid={`forecast-${h}-button`}
                          className="rounded-sm text-[10px] h-6 border-slate-200"
                        >
                          {generating === h ? <Loader2 className="h-3 w-3 animate-spin" /> : <TrendingUp className="h-3 w-3" />}
                        </Button>
                      </div>
                      {fc ? (
                        <>
                          <p className="font-mono text-xl font-bold text-slate-900">${fc.predicted_amount.toLocaleString()}</p>
                          <div className="flex items-center gap-1.5 mt-1.5">
                            <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full ${fc.confidence_pct >= 75 ? 'bg-green-500' : fc.confidence_pct >= 55 ? 'bg-amber-500' : 'bg-red-500'}`}
                                style={{ width: `${fc.confidence_pct}%` }}
                              />
                            </div>
                            <span className="text-[10px] font-mono text-slate-500">{fc.confidence_pct}%</span>
                          </div>
                          <p className="text-[10px] text-slate-400 mt-1.5 font-mono">
                            ${fc.confidence_lower.toLocaleString()} – ${fc.confidence_upper.toLocaleString()}
                          </p>
                        </>
                      ) : (
                        <p className="text-xs text-slate-300 mt-2">Click to generate forecast</p>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </TabsContent>

        {/* Secondary Costs Tab */}
        <TabsContent value="secondary">
          <div className="space-y-4">
            <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-semibold text-slate-800">
                    30-Day Secondary Cost Forecast
                  </CardTitle>
                  <Badge variant="outline" className="rounded-sm text-xs">
                    Total: ${(secondaryCosts?.total_predicted_30d || 0).toLocaleString()}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                {catChartData.length > 0 && (
                  <div className="h-48 mb-4">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={catChartData} margin={{ left: 10, bottom: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#64748b' }} />
                        <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
                        <RechartsTooltip contentStyle={{ borderRadius: 2, border: '1px solid #e2e8f0', fontSize: 12 }} formatter={v => `$${v.toLocaleString()}`} />
                        <Bar dataKey="predicted" radius={[2, 2, 0, 0]} barSize={32}>
                          {catChartData.map((e, i) => (
                            <Cell key={i} fill={e.anomaly ? '#E11D48' : '#0A2463'} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {Object.entries(categories).map(([cat, data]) => (
                    <div key={cat} className={`px-3 py-2.5 border rounded-sm ${data.anomaly_flag ? 'border-red-200 bg-red-50/30' : 'border-slate-100'}`} data-testid={`secondary-cost-${cat}`}>
                      <div className="flex items-center justify-between">
                        <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{cat.replace(/_/g, ' ')}</p>
                        {data.anomaly_flag && <AlertTriangle className="h-3 w-3 text-red-500" />}
                      </div>
                      <p className="font-mono text-sm font-bold text-slate-900 mt-1">${data.predicted_30d.toLocaleString()}</p>
                      <p className="text-[10px] text-slate-400 font-mono mt-0.5">
                        ${data.confidence_lower.toLocaleString()} – ${data.confidence_upper.toLocaleString()}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        {data.is_peak_season && (
                          <Badge variant="outline" className="rounded-sm text-[8px] border-amber-200 text-amber-700 bg-amber-50">Peak</Badge>
                        )}
                        <span className="text-[9px] text-slate-400">Hist: ${data.historical_mean.toLocaleString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Freight Rates Tab */}
        <TabsContent value="freight">
          <div className="space-y-4">
            <div className="flex justify-end">
              <Button
                onClick={handleGenerateFreight}
                disabled={generating === 'freight'}
                data-testid="generate-freight-button"
                className="rounded-sm bg-[#0A2463] hover:bg-[#0A2463]/90 text-white text-xs"
              >
                {generating === 'freight' ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
                Generate Freight Predictions
              </Button>
            </div>

            {freightChartData.length > 0 && (
              <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-slate-800">7-Day Spot Rate by Route ($/day)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-52">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={freightChartData} margin={{ left: 10, bottom: 30 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="route" tick={{ fontSize: 9, fill: '#64748b' }} angle={-15} textAnchor="end" height={50} />
                        <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
                        <RechartsTooltip contentStyle={{ borderRadius: 2, border: '1px solid #e2e8f0', fontSize: 12 }} formatter={v => `$${v.toLocaleString()}/day`} />
                        <Bar dataKey="spot_7d" fill="#0A2463" radius={[2, 2, 0, 0]} barSize={24} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}

            <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
              <CardContent className="p-0">
                {(freightHistory.length > 0 ? freightHistory : freightRates).length === 0 ? (
                  <div className="py-16 text-center text-sm text-slate-400">No freight predictions yet. Click to generate.</div>
                ) : (
                  <table className="w-full text-sm" data-testid="freight-rates-table">
                    <thead>
                      <tr className="border-b border-slate-100">
                        <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Route</th>
                        <th className="text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Horizon</th>
                        <th className="text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Spot ($/day)</th>
                        <th className="text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">TC ($/day)</th>
                        <th className="text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Confidence</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(freightHistory.length > 0 ? freightHistory : freightRates).slice(0, 30).map(p => (
                        <tr key={p.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                          <td className="px-5 py-2 text-xs font-medium text-slate-800">{p.route}</td>
                          <td className="px-5 py-2 text-xs font-mono text-right text-slate-500">{p.horizon_days}d</td>
                          <td className="px-5 py-2 text-xs font-mono text-right text-slate-900 font-bold">${parseFloat(p.predicted_spot_rate).toLocaleString()}</td>
                          <td className="px-5 py-2 text-xs font-mono text-right text-slate-600">${parseFloat(p.predicted_tc_rate).toLocaleString()}</td>
                          <td className="px-5 py-2 text-right">
                            <Badge variant="outline" className={`rounded-sm text-[9px] font-mono ${
                              parseFloat(p.confidence_pct) >= 80 ? 'border-green-300 text-green-700 bg-green-50' :
                              parseFloat(p.confidence_pct) >= 60 ? 'border-amber-300 text-amber-700 bg-amber-50' :
                              'border-red-300 text-red-700 bg-red-50'
                            }`}>
                              {parseFloat(p.confidence_pct).toFixed(0)}%
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
